/** Shared 270° arc geometry for GradientGauge + interactive CTL dial. */

export const GAUGE_START_DEG = 135;
export const GAUGE_TOTAL_DEG = 270;
export const GAUGE_SEGMENTS = 64;

/** ATP CTL target dial range */
export const CTL_GAUGE_MIN = 60;
export const CTL_GAUGE_MAX = 300;

/** Arc length for strokeDasharray progress (270° at radius r). */
export function gaugeArcLength(r: number): number {
  return (GAUGE_TOTAL_DEG / 360) * (2 * Math.PI * r);
}

export const GAUGE_GRAD_STOPS = ['#E040FB', '#7B68EE', '#448AFF', '#00D2FF', '#40C4FF'];

export function hexToRgb(hex: string) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m
    ? { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) }
    : { r: 0, g: 0, b: 0 };
}

export function lerpGaugeColor(t: number, stops: string[] = GAUGE_GRAD_STOPS): string {
  const n = stops.length - 1;
  const i = Math.min(Math.floor(t * n), n - 1);
  const lt = t * n - i;
  const a = hexToRgb(stops[i]);
  const b = hexToRgb(stops[i + 1]);
  return `rgb(${Math.round(a.r + (b.r - a.r) * lt)},${Math.round(a.g + (b.g - a.g) * lt)},${Math.round(a.b + (b.b - a.b) * lt)})`;
}

export function gaugePolar(cx: number, cy: number, r: number, deg: number) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

export function gaugeArcSegPath(cx: number, cy: number, r: number, a1: number, a2: number): string {
  const s = gaugePolar(cx, cy, r, a1);
  const e = gaugePolar(cx, cy, r, a2);
  return `M ${s.x.toFixed(3)} ${s.y.toFixed(3)} A ${r} ${r} 0 0 1 ${e.x.toFixed(3)} ${e.y.toFixed(3)}`;
}

export function buildGaugeTrackPath(cx: number, cy: number, r: number): string {
  const mid = GAUGE_START_DEG + GAUGE_TOTAL_DEG / 2;
  const end = GAUGE_START_DEG + GAUGE_TOTAL_DEG;
  const s = gaugePolar(cx, cy, r, GAUGE_START_DEG);
  const m = gaugePolar(cx, cy, r, mid);
  const e = gaugePolar(cx, cy, r, end);
  const f = (n: number) => n.toFixed(3);
  return `M ${f(s.x)} ${f(s.y)} A ${r} ${r} 0 0 1 ${f(m.x)} ${f(m.y)} A ${r} ${r} 0 0 1 ${f(e.x)} ${f(e.y)}`;
}

export function valueToGaugeRatio(value: number, min: number, max: number): number {
  if (max <= min) return 0;
  return Math.max(0, Math.min(1, (value - min) / (max - min)));
}

export function gaugeRatioToValue(ratio: number, min: number, max: number): number {
  return Math.round(min + Math.max(0, Math.min(1, ratio)) * (max - min));
}

export function touchAngleDeg(localX: number, localY: number, cx: number, cy: number): number {
  const dx = localX - cx;
  const dy = localY - cy;
  let deg = (Math.atan2(dx, -dy) * 180) / Math.PI;
  if (deg < 0) deg += 360;
  return deg;
}

export function angleDegToGaugeValue(angleDeg: number, min: number, max: number): number {
  let a = angleDeg;
  const endDeg = (GAUGE_START_DEG + GAUGE_TOTAL_DEG) % 360;
  if (a < endDeg + 20) a += 360;
  const clamped = Math.max(GAUGE_START_DEG, Math.min(GAUGE_START_DEG + GAUGE_TOTAL_DEG, a));
  const ratio = (clamped - GAUGE_START_DEG) / GAUGE_TOTAL_DEG;
  return gaugeRatioToValue(ratio, min, max);
}

export function isNearGaugeArc(
  localX: number,
  localY: number,
  cx: number,
  cy: number,
  r: number,
  strokeW: number,
): boolean {
  const dx = localX - cx;
  const dy = localY - cy;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const band = strokeW * 2.2;
  return dist >= r - band && dist <= r + band;
}

export function buildGaugeGradientSegments(
  cx: number,
  cy: number,
  r: number,
  fillRatio: number,
): { path: string; color: string }[] {
  const filledDeg = fillRatio * GAUGE_TOTAL_DEG;
  if (filledDeg <= 0) return [];
  const segDeg = filledDeg / GAUGE_SEGMENTS;
  return Array.from({ length: GAUGE_SEGMENTS }, (_, i) => {
    const t = i / (GAUGE_SEGMENTS - 1);
    const a1 = GAUGE_START_DEG + i * segDeg;
    const a2 = a1 + segDeg + 0.35;
    return {
      path: gaugeArcSegPath(cx, cy, r, a1, a2),
      color: lerpGaugeColor(t),
    };
  });
}
